import { useState, useEffect, useMemo, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
	LockClosedIcon,
	ExclamationTriangleIcon,
	EyeIcon,
	EyeSlashIcon,
	CheckIcon,
	LightBulbIcon,
	ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Button } from '../../components/ui';
import { authApi, ApiError } from '../../api';
import useAuthStore from '../../stores/authStore';

// Password requirements configuration
const PASSWORD_REQUIREMENTS = {
	length: {
		regex: /.{8,}/,
		label: 'Minimum 8 characters',
	},
	uppercase: {
		regex: /[A-Z]/,
		label: 'At least 1 uppercase letter (A-Z)',
	},
	lowercase: {
		regex: /[a-z]/,
		label: 'At least 1 lowercase letter (a-z)',
	},
	number: {
		regex: /[0-9]/,
		label: 'At least 1 number (0-9)',
	},
	special: {
		regex: /[!@#$%^&*(),.?":{}|<>]/,
		label: 'At least 1 special character (!@#$%^&*)',
	},
};

type PasswordStrength = 'not-set' | 'weak' | 'medium' | 'strong';

interface ChangePasswordProps {
	redirectTo?: string;
}

const ChangePassword = ({ redirectTo = '/providers/dashboard' }: ChangePasswordProps) => {
	const navigate = useNavigate();
	const { refreshSession } = useAuthStore();
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Check which requirements are met
	const requirementsMet = useMemo(() => {
		return Object.entries(PASSWORD_REQUIREMENTS).reduce(
			(acc, [key, { regex }]) => {
				acc[key as keyof typeof PASSWORD_REQUIREMENTS] = regex.test(newPassword);
				return acc;
			},
			{} as Record<keyof typeof PASSWORD_REQUIREMENTS, boolean>
		);
	}, [newPassword]);

	// Count how many requirements are met
	const metCount = useMemo(() => {
		return Object.values(requirementsMet).filter(Boolean).length;
	}, [requirementsMet]);

	// Calculate password strength
	const passwordStrength: PasswordStrength = useMemo(() => {
		if (newPassword.length === 0) return 'not-set';
		if (metCount <= 2) return 'weak';
		if (metCount <= 4) return 'medium';
		return 'strong';
	}, [newPassword.length, metCount]);

	// Check if all requirements are met
	const allRequirementsMet = metCount === Object.keys(PASSWORD_REQUIREMENTS).length;

	// Check if passwords match
	const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

	// Check if form is valid
	const isFormValid = currentPassword.length > 0 && allRequirementsMet && passwordsMatch;

	// Get strength text
	const getStrengthText = () => {
		switch (passwordStrength) {
			case 'weak':
				return 'Weak';
			case 'medium':
				return 'Medium';
			case 'strong':
				return 'Strong';
			default:
				return 'Not Set';
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		if (!isFormValid) return;

		setIsSubmitting(true);

		try {
			const response = await authApi.changePassword({
				currentPassword,
				newPassword,
				confirmPassword,
			});

			if (response.success) {
				// Refresh session with new token and updated user data
				refreshSession(response.token, {
					name: response.name,
					image: response.image,
					passwordChangeRequired: false,
				});

				toast.success('Password changed successfully!');
				navigate(redirectTo, { state: { passwordJustChanged: true } });
			} else {
				toast.error(response.message || 'Failed to change password');
			}
		} catch (error) {
			const apiError = error as ApiError;
			toast.error(apiError.message || 'Failed to change password');
		} finally {
			setIsSubmitting(false);
		}
	};

	// Auto-focus on new password field
	useEffect(() => {
		const input = document.getElementById('newPassword');
		if (input) input.focus();
	}, []);

	return (
		<div className="provider-page">
			<div className="provider-page__card">
				{/* Header */}
				<div className="provider-page__header">
					<div className="provider-page__logo">
						<LockClosedIcon className="w-10 h-10" />
					</div>
					<h1 className="provider-page__title">Password Change Required</h1>
					<p className="provider-page__subtitle">First-time login detected</p>
				</div>

				{/* Body */}
				<div className="provider-page__body">
					{/* Warning Alert */}
					<div className="provider-alert provider-alert--warning">
						<ExclamationTriangleIcon className="provider-alert__icon" />
						<div className="provider-alert__content">
							<div className="provider-alert__title">Security Notice</div>
							<div className="provider-alert__text">
								For your security, you must create a new password before accessing
								your account. Your temporary password can only be used once.
							</div>
						</div>
					</div>

					{/* Password Change Form */}
					<form onSubmit={handleSubmit}>
						{/* Current Password (temporary password) */}
						<div className="provider-form__group">
							<label className="provider-form__label">
								Current Password<span className="provider-form__required">*</span>
							</label>
							<div className="provider-form__input-wrapper">
								<input
									type={showCurrentPassword ? 'text' : 'password'}
									id="currentPassword"
									className="provider-form__input provider-form__input--has-icon"
									placeholder="Enter your temporary password"
									value={currentPassword}
									onChange={e => setCurrentPassword(e.target.value)}
									autoComplete="current-password"
								/>
								<button
									type="button"
									className="provider-form__toggle-password"
									onClick={() => setShowCurrentPassword(!showCurrentPassword)}
								>
									{showCurrentPassword ? (
										<EyeSlashIcon className="w-5 h-5" />
									) : (
										<EyeIcon className="w-5 h-5" />
									)}
								</button>
							</div>
						</div>

						{/* New Password */}
						<div className="provider-form__group">
							<label className="provider-form__label">
								Create New Password
								<span className="provider-form__required">*</span>
							</label>
							<div className="provider-form__input-wrapper">
								<input
									type={showNewPassword ? 'text' : 'password'}
									id="newPassword"
									className="provider-form__input provider-form__input--has-icon"
									placeholder="Enter your new secure password"
									value={newPassword}
									onChange={e => setNewPassword(e.target.value)}
									autoComplete="new-password"
								/>
								<button
									type="button"
									className="provider-form__toggle-password"
									onClick={() => setShowNewPassword(!showNewPassword)}
								>
									{showNewPassword ? (
										<EyeSlashIcon className="w-5 h-5" />
									) : (
										<EyeIcon className="w-5 h-5" />
									)}
								</button>
							</div>

							{/* Password Requirements */}
							<div className="password-requirements">
								<div className="password-requirements__title">
									<CheckIcon className="w-5 h-5" />
									Password Requirements
								</div>
								<ul className="password-requirements__list">
									{Object.entries(PASSWORD_REQUIREMENTS).map(
										([key, { label }]) => {
											const isMet =
												requirementsMet[
													key as keyof typeof PASSWORD_REQUIREMENTS
												];
											return (
												<li
													key={key}
													className={`password-requirements__item ${
														isMet
															? 'password-requirements__item--met'
															: ''
													}`}
												>
													<span
														className={`password-requirements__icon ${
															isMet
																? 'password-requirements__icon--met'
																: ''
														}`}
													>
														{isMet ? (
															<CheckIcon className="w-3 h-3" />
														) : (
															<span className="w-2 h-2 rounded-full bg-current" />
														)}
													</span>
													<span>{label}</span>
												</li>
											);
										}
									)}
								</ul>

								{/* Password Strength Meter */}
								<div className="password-strength">
									<div className="password-strength__label">
										<span>Password Strength:</span>
										<span
											className={`password-strength__text password-strength__text--${passwordStrength}`}
										>
											{getStrengthText()}
										</span>
									</div>
									<div className="password-strength__bar-container">
										<div
											className={`password-strength__bar password-strength__bar--${passwordStrength}`}
										/>
									</div>
								</div>
							</div>
						</div>

						{/* Confirm Password */}
						<div className="provider-form__group">
							<label className="provider-form__label">
								Confirm New Password
								<span className="provider-form__required">*</span>
							</label>
							<div className="provider-form__input-wrapper">
								<input
									type={showConfirmPassword ? 'text' : 'password'}
									id="confirmPassword"
									className={`provider-form__input provider-form__input--has-icon ${
										confirmPassword.length > 0
											? passwordsMatch
												? 'provider-form__input--success'
												: 'provider-form__input--error'
											: ''
									}`}
									placeholder="Re-enter your new password"
									value={confirmPassword}
									onChange={e => setConfirmPassword(e.target.value)}
									autoComplete="new-password"
								/>
								<button
									type="button"
									className="provider-form__toggle-password"
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
								>
									{showConfirmPassword ? (
										<EyeSlashIcon className="w-5 h-5" />
									) : (
										<EyeIcon className="w-5 h-5" />
									)}
								</button>
							</div>
							{confirmPassword.length > 0 && !passwordsMatch && (
								<div className="provider-form__message provider-form__message--error">
									<ExclamationTriangleIcon className="w-4 h-4" />
									<span>Passwords do not match</span>
								</div>
							)}
							{passwordsMatch && (
								<div className="provider-form__message provider-form__message--success">
									<CheckCircleIcon className="w-4 h-4" />
									<span>Passwords match perfectly</span>
								</div>
							)}
						</div>

						{/* Submit Button */}
						<Button
							type="submit"
							variant="primary"
							className="w-full"
							size="lg"
							disabled={!isFormValid || isSubmitting}
							isLoading={isSubmitting}
						>
							{isSubmitting ? 'Changing Password...' : 'Change Password & Continue'}
						</Button>
					</form>

					{/* Security Tips */}
					<div className="security-tips">
						<div className="security-tips__title">
							<LightBulbIcon className="w-5 h-5" />
							Password Security Tips
						</div>
						<ul className="security-tips__list">
							<li className="security-tips__item">
								Use a unique password you don't use elsewhere
							</li>
							<li className="security-tips__item">
								Avoid personal information (names, birthdays)
							</li>
							<li className="security-tips__item">
								Consider using a password manager
							</li>
							<li className="security-tips__item">
								Never share your password with anyone
							</li>
						</ul>
					</div>
				</div>

				{/* Footer */}
				<div className="provider-page__footer">
					<p className="flex items-center justify-center gap-1">
						<ShieldCheckIcon className="w-4 h-4 inline" />
						This is a secure connection. Your password is encrypted.
					</p>
					<p>&copy; 2025 Beauty Biz. All rights reserved.</p>
				</div>
			</div>
		</div>
	);
};

export default ChangePassword;
